import { useState } from 'react'
import { Activity, Search, Loader2, Plus, Pencil, Trash2, LogIn, LogOut, Eye, ChevronDown, ChevronRight } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useActivityLogs } from '@/lib/data/hooks/useActivityLogs'
import type { ActivityLog } from '@/lib/data'

interface ActivityViewProps {
  client: SupabaseClient | null
}

const ACTION_CONFIG = {
  create: { icon: Plus, color: 'bg-emerald-500/10 text-emerald-400', label: 'Create' },
  update: { icon: Pencil, color: 'bg-blue-500/10 text-blue-400', label: 'Update' },
  delete: { icon: Trash2, color: 'bg-red-500/10 text-red-400', label: 'Delete' },
  login: { icon: LogIn, color: 'bg-purple-500/10 text-purple-400', label: 'Login' },
  logout: { icon: LogOut, color: 'bg-gray-500/10 text-gray-400', label: 'Logout' },
  view: { icon: Eye, color: 'bg-amber-500/10 text-amber-400', label: 'View' },
}

export function ActivityView({ client }: ActivityViewProps) {
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())

  const { logs, isLoading, error } = useActivityLogs(client, {
    search,
    action: actionFilter,
  })

  const toggleExpanded = (id: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Activity size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view activity logs</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Activity Logs</h2>
        <p className="text-[var(--text-muted)] text-sm">{logs.length} entries</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by table or action..."
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </div>

        <div className="flex gap-2">
          {Object.entries(ACTION_CONFIG).map(([action, config]) => {
            const Icon = config.icon
            return (
              <button
                key={action}
                onClick={() => setActionFilter(actionFilter === action ? '' : action)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  actionFilter === action
                    ? config.color
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <Icon size={14} />
                {config.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Logs List */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No activity logs found
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {logs.map((log) => (
              <ActivityLogRow
                key={log.id}
                log={log}
                isExpanded={expandedLogs.has(log.id)}
                onToggle={() => toggleExpanded(log.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface ActivityLogRowProps {
  log: ActivityLog
  isExpanded: boolean
  onToggle: () => void
}

function ActivityLogRow({ log, isExpanded, onToggle }: ActivityLogRowProps) {
  const config = ACTION_CONFIG[log.action as keyof typeof ACTION_CONFIG] || ACTION_CONFIG.view
  const Icon = config.icon
  const hasChanges = log.changes && Object.keys(log.changes).length > 0

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
  }

  const { date, time } = formatDate(log.created_at)

  return (
    <div className="hover:bg-[var(--bg-element)]">
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-center gap-4"
        disabled={!hasChanges}
      >
        {/* Expand Icon */}
        <div className="w-5 h-5 flex items-center justify-center text-[var(--text-muted)]">
          {hasChanges ? (
            isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />
          ) : (
            <div className="w-1 h-1 rounded-full bg-[var(--text-muted)]" />
          )}
        </div>

        {/* Action Badge */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
          <Icon size={12} />
          {config.label}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {log.table_name && (
              <span className="text-[var(--text-main)] font-medium">{log.table_name}</span>
            )}
            {log.record_id && (
              <span className="text-[var(--text-muted)] text-sm font-mono truncate max-w-[120px]">
                #{log.record_id.slice(0, 8)}
              </span>
            )}
          </div>
          {log.admin_email && (
            <p className="text-[var(--text-muted)] text-sm">{log.admin_email}</p>
          )}
        </div>

        {/* Timestamp */}
        <div className="text-right text-sm">
          <p className="text-[var(--text-muted)]">{date}</p>
          <p className="text-[var(--text-muted)] text-xs">{time}</p>
        </div>
      </button>

      {/* Expanded Changes */}
      {isExpanded && hasChanges && (
        <div className="px-4 pb-4 pl-14">
          <div className="bg-[var(--bg-element)] rounded-lg p-4 overflow-x-auto">
            <pre className="text-xs text-[var(--text-main)] whitespace-pre-wrap">
              {JSON.stringify(log.changes, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}
